import { useCallback } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { AuthenticationStatus } from "../store/slices/authSlice";
import { addBlockedUser, clearBlockedUsers, removeBlockedUser, selectBlockedUserIds, setBlockedUsers } from "../store/slices/blocksSlice";
import { connectionRepository } from "../repositories/ConnectionRepository";

// Bumped on every change to the blocked list, so a fetch that started before
// a block/unblock, logout or account switch doesn't overwrite newer state.
let blocksVersion = 0;

/** Ids of the users blocked by the current user. Cheap to call per message. */
export function useBlockedUserIds(): ReadonlySet<string> {
    return useSelector(selectBlockedUserIds);
}

export function useBlockedUsers() {
    const dispatch = useDispatch<AppDispatch>();
    const store = useStore<RootState>();
    const blockedUserIds = useBlockedUserIds();

    const refreshBlockedUsers = useCallback(async () => {
        const { auth } = store.getState();
        const ownerUserId = auth.currentUserId;

        if(!ownerUserId || auth.status !== AuthenticationStatus.AUTHENTICATED) {
            return;
        }

        const version = ++blocksVersion;

        try {
            const ids = await connectionRepository.getBlockedUserIds();
            const latestAuth = store.getState().auth;

            if(version !== blocksVersion ||
                latestAuth.currentUserId !== ownerUserId ||
                latestAuth.status !== AuthenticationStatus.AUTHENTICATED) {
                return;
            }

            dispatch(setBlockedUsers({ ownerUserId, blockedUserIds: ids }));
        } catch (error) {
            console.error('Error fetching blocked users:', error);
        }
    }, [dispatch, store]);

    /** Call after a block request succeeded. */
    const onUserBlocked = useCallback((blockedUserId: string) => {
        const ownerUserId = store.getState().auth.currentUserId;
        if(!ownerUserId) return;
        blocksVersion++;
        dispatch(addBlockedUser({ ownerUserId, blockedUserId }));
    }, [dispatch, store]);

    /** Call after an unblock request succeeded. */
    const onUserUnblocked = useCallback((blockedUserId: string) => {
        const ownerUserId = store.getState().auth.currentUserId;
        if(!ownerUserId) return;
        blocksVersion++;
        dispatch(removeBlockedUser({ ownerUserId, blockedUserId }));
    }, [dispatch, store]);

    const resetBlockedUsers = useCallback(() => {
        blocksVersion++;
        dispatch(clearBlockedUsers());
    }, [dispatch]);

    return {
        blockedUserIds,
        refreshBlockedUsers,
        onUserBlocked,
        onUserUnblocked,
        resetBlockedUsers,
    };
}
